// Dialog para agregar o corregir UN referido desde el cajón. Valida local antes de mandar y pega
// los codes del silo al campo que hay que corregir.
import { useState, type SyntheticEvent } from 'react';
import { toast } from 'sonner';
import { Button, Input, Modal, PhoneInput } from '@/kit';
import type { BackofficeCustomerReferral } from '@/api/services/retail-wizard/customerReferrals.client';
import {
    formOf,
    isReferralNotFound,
    REFERRAL_DIALOG_COPY as COPY,
    referralPatchOf,
    serverFieldError,
    validateReferralForm,
    type ReferralForm,
    type ReferralFormErrors,
    type ReferralFormField,
} from './customerReferrals';
import { useCreateCustomerReferral, useUpdateCustomerReferral } from './useCustomerReferrals';

export interface ReferralDialogProps {
    retailCustomerId: string;
    retailerId?: string;
    customerName: string;
    customerPhone: string | null;
    /** El referido a corregir; `null` = agregar uno nuevo. */
    editing: BackofficeCustomerReferral | null;
    /** Todos los referidos del cliente, para avisar de un teléfono repetido. */
    referrals: readonly BackofficeCustomerReferral[];
    onClose: () => void;
}

export function ReferralDialog({
    retailCustomerId,
    retailerId,
    customerName,
    customerPhone,
    editing,
    referrals,
    onClose,
}: ReferralDialogProps) {
    const [form, setForm] = useState<ReferralForm>(() => formOf(editing));
    const [errors, setErrors] = useState<ReferralFormErrors>({});
    const [failure, setFailure] = useState<string | null>(null);
    const create = useCreateCustomerReferral(retailCustomerId, retailerId);
    const update = useUpdateCustomerReferral(retailCustomerId, retailerId);
    const isSaving = create.isPending || update.isPending;
    const others = referrals.filter((r) => r.referralId !== editing?.referralId);

    const change = (field: ReferralFormField, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const onError = (error: unknown) => {
        const fieldError = serverFieldError(error);
        if (fieldError) {
            setErrors({ [fieldError.field]: fieldError.message });
            return;
        }
        setFailure(isReferralNotFound(error) ? COPY.notFound : COPY.saveFailed);
    };

    const submit = (event: SyntheticEvent) => {
        event.preventDefault();
        setFailure(null);
        const local = validateReferralForm(form, { customerPhone, others });
        setErrors(local);
        if (Object.keys(local).length > 0) return;

        if (editing) {
            const patch = referralPatchOf(editing, form);
            if (!patch) {
                onClose();
                return;
            }
            update.mutate(
                { referralId: editing.referralId, patch },
                {
                    onSuccess: () => {
                        toast.success(COPY.updated);
                        onClose();
                    },
                    onError,
                },
            );
            return;
        }
        const email = form.email.trim();
        create.mutate(
            { name: form.name.trim(), phone: form.phone, ...(email && { email }) },
            {
                onSuccess: () => {
                    toast.success(COPY.added);
                    onClose();
                },
                onError,
            },
        );
    };

    return (
        <Modal
            isOpen
            onClose={onClose}
            title={editing ? COPY.editTitle : COPY.addTitle}
            description={
                editing ? COPY.editDescription(customerName) : COPY.addDescription(customerName)
            }
        >
            <form noValidate onSubmit={submit} className="grid gap-3.5">
                <Input
                    label={COPY.nameLabel}
                    placeholder={COPY.namePlaceholder}
                    autoComplete="off"
                    maxLength={120}
                    required
                    value={form.name}
                    error={errors.name}
                    onChange={(e) => {
                        change('name', e.target.value);
                    }}
                />
                <PhoneInput
                    label={COPY.phoneLabel}
                    placeholder={COPY.phonePlaceholder}
                    autoComplete="off"
                    required
                    value={form.phone}
                    error={errors.phone}
                    onValueChange={(digits) => {
                        change('phone', digits);
                    }}
                />
                <Input
                    label={COPY.emailLabel}
                    type="email"
                    placeholder={COPY.emailPlaceholder}
                    autoComplete="off"
                    value={form.email}
                    error={errors.email}
                    onChange={(e) => {
                        change('email', e.target.value);
                    }}
                />
                <p className="rounded-md border border-info-soft-border bg-info-soft px-3 py-2 text-[11.5px] leading-snug text-info">
                    {editing ? COPY.editNote : COPY.addNote}
                </p>
                {failure ? (
                    <p role="alert" className="text-[12px] text-destructive">
                        {failure}
                    </p>
                ) : null}
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
                        {COPY.cancel}
                    </Button>
                    <Button type="submit" isLoading={isSaving}>
                        {editing ? COPY.submitEdit : COPY.submitAdd}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
